import { Box, useTheme } from '@mui/material';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// An axis name as a label: each word capitalised, acronyms kept ("HVAC & SYSTEMS" -> "HVAC & Systems")
const ACRONYMS = new Set(['HVAC', 'HOA', 'NHD', 'AVM', 'ADU']);
const axisLabel = (raw: string) =>
  raw
    .replace(/_/g, ' ')
    .split(' ')
    .map((w) => (ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');

export interface RadarChartDataPoint {
  label: string;
  value: number; // 0-100 scale
  isNa?: boolean; // true when category has no inspection findings
  /** Draws a pulsing white marker at this vertex. */
  highlight?: boolean;
}

export interface RadarChartDataSeries {
  data: RadarChartDataPoint[];
  fromData?: RadarChartDataPoint[];
  strokeColor?: string;
  fillColor?: string;
  strokeWidth?: number;
  strokeDasharray?: string;
  animatePathMs?: number;
  /** Per-edge colors — one per vertex (edge connects vertex[i] → vertex[i+1 % n]).
   *  When provided, individual colored line segments replace the single-color polygon stroke. */
  segmentColors?: string[];
}

export interface RadarChartProps {
  data?: RadarChartDataPoint[];
  series?: RadarChartDataSeries[];
  size?: number;
  strokeColor?: string;
  fillColor?: string;
  gridColor?: string;
  gridLevels?: number;
  labelColor?: string;
  /** Per-instance axis-label color without changing legacy radar consumers. */
  axisLabelColor?: string;
  showLabels?: boolean;
  /** When true, each plotted vertex becomes a tap/hover target showing a
   *  label + score value tooltip (mirrors HealthRings' hover tooltip).
   *  Useful when showLabels is false and axis text isn't available to tap. */
  showValueTooltips?: boolean;
  /** Maps uppercase label text → tooltip description shown on hover */
  labelTooltips?: Record<string, string>;
  /** When true, renders a frosted-glass label-only tooltip (no value) at
   *  every vertex simultaneously, centered on that vertex's actual plotted
   *  point. Externally controlled — the caller owns the hover trigger (e.g.
   *  hovering a container larger than the chart itself), the chart just
   *  renders. Independent of showLabels/showValueTooltips. */
  forceShowAllLabels?: boolean;
  /** Extra fixed-px nudge per axis index for both persistent axis labels and
   *  forceShowAllLabels overlays. */
  labelOffsetOverrides?: Record<number, { x?: number; y?: number }>;
  animateOnDataChange?: boolean;
  /**
   * When provided, tooltips portal into this element (position:absolute)
   * and are clamped to its bounds rather than the viewport.
   */
  tooltipContainerRef?: React.RefObject<HTMLElement | null>;
  /** Corner rounding radius (px) applied to the data series shape — 0 for sharp points */
  cornerRadius?: number;
  /** Overrides the default 0.30 (labels shown) / 0.46 (labels hidden) radius
   *  fraction. Scoped per-instance — omit to keep the shared default so
   *  other RadarChart consumers are unaffected. */
  radiusFraction?: number;
  /** Matte form for the score shape: faceted, lit from the upper left like the card sheen; tone only, no gloss,
   *  shadow or inner lines */
  contoured?: boolean;
  /** Each axis label carries its score ("Structural 87"); the weakest axis is marked in its colour */
  axisValues?: boolean;
  /** Rotates the first axis without changing the shared top-point default. */
  startAngleDegrees?: number;
}

/** Parse a hex color (#RRGGBB or #RGB) into [r, g, b] */
const parseHex = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  if (h.length === 3) {
    return [parseInt(h[0] + h[0], 16), parseInt(h[1] + h[1], 16), parseInt(h[2] + h[2], 16)];
  }
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

/** Linearly interpolate between two hex colors at ratio t ∈ [0,1] */
const lerpColor = (a: string, b: string, t: number): string => {
  const [r1, g1, b1] = parseHex(a);
  const [r2, g2, b2] = parseHex(b);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const bl = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${bl})`;
};

type Point = { x: number; y: number };

/** Point at distance `dist` from `from`, along the line toward `to` (clamped to the midpoint) */
const pullBack = (from: Point, to: Point, dist: number): Point => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const clamped = Math.min(dist, len / 2);
  return { x: from.x + (dx / len) * clamped, y: from.y + (dy / len) * clamped };
};

/** Build a closed SVG path `d` string for a polygon with rounded corners (quadratic curves at each vertex) */
const buildRoundedPathFromPoints = (pts: Point[], radius: number): string => {
  const n = pts.length;
  if (n < 3 || radius <= 0) {
    return `M ${pts.map((p) => `${p.x},${p.y}`).join(' L ')} Z`;
  }
  const starts = pts.map((p, i) => pullBack(p, pts[(i - 1 + n) % n], radius));
  const ends = pts.map((p, i) => pullBack(p, pts[(i + 1) % n], radius));
  const cmds: string[] = [`M ${ends[n - 1].x},${ends[n - 1].y}`];
  for (let i = 0; i < n; i++) {
    cmds.push(`L ${starts[i].x},${starts[i].y}`);
    cmds.push(`Q ${pts[i].x},${pts[i].y} ${ends[i].x},${ends[i].y}`);
  }
  cmds.push('Z');
  return cmds.join(' ');
};

const RadarChart = ({
  data,
  series,
  size = 400,
  strokeColor,
  fillColor,
  gridColor,
  gridLevels = 5,
  labelColor: _labelColor,
  axisLabelColor,
  showLabels = true,
  showValueTooltips = false,
  labelTooltips,
  forceShowAllLabels = false,
  labelOffsetOverrides,
  animateOnDataChange = true,
  tooltipContainerRef,
  cornerRadius = 5,
  radiusFraction,
  contoured = false,
  axisValues = false,
  startAngleDegrees = -90,
}: RadarChartProps) => {
  const theme = useTheme();
  const [tooltip, setTooltip] = useState<
    | { kind: 'text'; text: string; x: number; y: number; yBottom: number }
    | {
        kind: 'value';
        label: string;
        value: number;
        isNa: boolean;
        x: number;
        y: number;
        yBottom: number;
      }
    | null
  >(null);

  // Touch-primary devices (mobile, devtools mobile mode, tablets) don't fire
  // mouseenter reliably on a tap — synthetic mouse events arrive together
  // with mouseleave, so the tooltip flashes shut. On those devices we route
  // through onClick instead and dismiss on outside tap.
  const isTouchPrimary = (): boolean =>
    typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

  // Touch-only outside-tap dismiss while a tooltip is open. setTimeout(0)
  // skips the click that opened it.
  useEffect(() => {
    if (!tooltip || !isTouchPrimary()) return;
    const onOutside = () => setTooltip(null);
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', onOutside);
      document.addEventListener('touchstart', onOutside);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
    };
  }, [tooltip]);

  // Animation: bump key whenever data values change to retrigger grow-from-center
  const [animKey, setAnimKey] = useState(0);
  const dataFingerprint = useMemo(
    () => JSON.stringify((series ?? [{ data }]).map((s) => s.data?.map((d) => d.value))),
    [series, data],
  );
  const prevFingerprint = useRef(dataFingerprint);
  useEffect(() => {
    if (animateOnDataChange && dataFingerprint !== prevFingerprint.current) {
      prevFingerprint.current = dataFingerprint;
      setAnimKey((k) => k + 1);
    }
  }, [animateOnDataChange, dataFingerprint]);

  // Convert single data prop to series format for unified handling
  const dataSeries: RadarChartDataSeries[] = useMemo(() => {
    if (series && series.length > 0) return series;
    if (data && data.length > 0) {
      return [
        {
          data,
          strokeColor: strokeColor || theme.palette.success.main,
          fillColor: fillColor || `${theme.palette.success.main}20`,
          strokeWidth: 3,
        },
      ];
    }
    return [];
  }, [series, data, strokeColor, fillColor, theme]);

  const defaultGridColor = gridColor || theme.palette.text.secondary2;

  const center = size / 2;
  // 0.30 leaves room for the label ring around the grid; with labels hidden
  // that margin is wasted space, so reclaim it and fill the box instead.
  // radiusFraction overrides this per-instance when a caller wants to push
  // past the shared default without affecting other RadarChart consumers.
  const radius = size * (radiusFraction ?? (showLabels ? 0.3 : 0.46));
  const labelOffset = radius + 32;

  // Use labels from first series
  const labels = dataSeries[0]?.data.map((d) => d.label) || [];
  const numPoints = labels.length;
  const angleStep = numPoints > 0 ? (2 * Math.PI) / numPoints : 0;
  const startAngle = (startAngleDegrees * Math.PI) / 180;

  const getPointPosition = (index: number, value: number) => {
    const angle = startAngle + index * angleStep;
    const distance = (value / 100) * radius;
    return {
      x: center + distance * Math.cos(angle),
      y: center + distance * Math.sin(angle),
    };
  };

  const getGridPoints = (level: number): Point[] => {
    const points: Point[] = [];
    for (let i = 0; i < numPoints; i++) {
      points.push(getPointPosition(i, (level / gridLevels) * 100));
    }
    return points;
  };

  const axisLines = useMemo(
    () =>
      labels.map((_, index) => {
        const pos = getPointPosition(index, 100);
        return { x1: center, y1: center, x2: pos.x, y2: pos.y };
      }),
    [labels, center, radius, startAngle, angleStep],
  );

  const buildTooltipCoords = (rect: DOMRect) => {
    if (tooltipContainerRef?.current) {
      const cRect = tooltipContainerRef.current.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2 - cRect.left,
        y: rect.top - cRect.top,
        yBottom: rect.bottom - cRect.top,
      };
    }
    return { x: rect.left + rect.width / 2, y: rect.top, yBottom: rect.bottom };
  };

  // Label positions as % of container for HTML overlay
  const labelPositions = useMemo(
    () =>
      labels.map((label, index) => {
        const angle = startAngle + index * angleStep;
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);
        const extra = labelOffsetOverrides?.[index];
        const x = center + labelOffset * cosA + (extra?.x ?? 0);
        const y = center + labelOffset * sinA + (extra?.y ?? 0);
        // 'end' for left side, 'start' for right side, 'middle' for top/bottom
        const align: 'start' | 'middle' | 'end' =
          cosA < -0.1 ? 'end' : cosA > 0.1 ? 'start' : 'middle';
        return { label, x, y, align };
      }),
    [labels, center, labelOffset, startAngle, angleStep, labelOffsetOverrides],
  );

  // The score shape as a matte, faceted form: one wedge per edge from the centre, each shaded by how it faces the
  // upper-left light (the card sheen's). Tone only, no inner lines, so nothing competes with the grid
  const contourClipId = `radar-contour-${useId().replace(/:/g, '')}`;
  const renderContour = (points: Array<{ x: number; y: number }>, outline: string) => {
    const light = { x: -0.55, y: -0.83 };
    return (
      <g pointerEvents="none">
        <defs>
          <clipPath id={contourClipId}>
            <path d={outline} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${contourClipId})`}>
          {points.map((p, i) => {
            const q = points[(i + 1) % points.length];
            const mx = (p.x + q.x) / 2 - center;
            const my = (p.y + q.y) / 2 - center;
            const len = Math.hypot(mx, my) || 1;
            const facing = (mx / len) * light.x + (my / len) * light.y;
            return (
              <polygon
                key={`facet-${i}`}
                points={`${center},${center} ${p.x},${p.y} ${q.x},${q.y}`}
                fill={facing > 0 ? '#ffffff' : '#000000'}
                fillOpacity={facing > 0 ? 0.14 * facing : 0.24 * -facing}
              />
            );
          })}
        </g>
      </g>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: size,
        aspectRatio: '1',
        position: 'relative',
        mx: 'auto',
        mt: '-45px',
        mb: '-10px',
        pointerEvents: 'none',
      }}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width="100%"
        height="100%"
        style={{ overflow: 'visible' }}>
        <style>{`
          @keyframes radarGrow {
            from { transform: scale(0); opacity: 0; }
            to   { transform: scale(1); opacity: 1; }
          }
          @keyframes radarRepairPreview {
            0%   { transform: scale(0.94); opacity: 0; filter: saturate(0.75); }
            72%  { transform: scale(1.025); opacity: 1; filter: saturate(1.08); }
            100% { transform: scale(1); opacity: 1; filter: saturate(1); }
          }
          @keyframes radarPointPulse {
            0%   { transform: scale(0.65); opacity: 0.9; }
            75%  { transform: scale(2.25); opacity: 0; }
            100% { transform: scale(2.25); opacity: 0; }
          }
        `}</style>
        {/* Grid levels */}
        {Array.from({ length: gridLevels }, (_, i) => i + 1).map((level) => (
          <path
            key={`grid-${level}`}
            d={buildRoundedPathFromPoints(getGridPoints(level), cornerRadius)}
            fill={`rgba(255,255,255,${0.04 + (gridLevels - level) * 0.02})`}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={1}
          />
        ))}

        {/* Axis lines — dashed + dimmed for N/A categories */}
        {axisLines.map((line, index) => {
          const isNa = dataSeries[0]?.data[index]?.isNa ?? false;
          return (
            <line
              key={`axis-${index}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={defaultGridColor}
              strokeWidth={1}
              strokeDasharray={isNa ? '4,3' : undefined}
              opacity={isNa ? 0.12 : 0.2}
            />
          );
        })}

        {/* Data series */}
        {dataSeries.map((s, seriesIndex) => {
          const seriesStrokeColor = s.strokeColor || theme.palette.success.main;
          const seriesFillColor = s.fillColor || 'transparent';
          const seriesStrokeWidth = s.strokeWidth || 3;
          const seriesStrokeDasharray = s.strokeDasharray;
          const seriesAnimatePathMs = s.animatePathMs ?? 0;
          const dataPoints = s.data.map((point, index) =>
            getPointPosition(index, point.isNa ? 77 : point.value),
          );
          const fromDataPoints = s.fromData?.map((point, index) =>
            getPointPosition(index, point.isNa ? 77 : point.value),
          );
          const roundedPath = buildRoundedPathFromPoints(dataPoints, cornerRadius);
          const fromRoundedPath = s.fromData
            ? buildRoundedPathFromPoints(fromDataPoints ?? [], cornerRadius)
            : null;
          // Per-edge endpoints pulled back from each vertex by cornerRadius, so adjacent
          // edges stop short of the vertex and a small rounded joint (below) fills the gap.
          const edgePulled = s.data.map((_, i) => {
            const nextI = (i + 1) % s.data.length;
            const isNaEdge = !!(s.data[i].isNa || s.data[nextI].isNa);
            if (isNaEdge) return { p1: dataPoints[i], p2: dataPoints[nextI], isNaEdge };
            return {
              p1: pullBack(dataPoints[i], dataPoints[nextI], cornerRadius),
              p2: pullBack(dataPoints[nextI], dataPoints[i], cornerRadius),
              isNaEdge,
            };
          });
          const fromEdgePulled = fromDataPoints
            ? s.data.map((_, i) => {
                const nextI = (i + 1) % s.data.length;
                const p1 = fromDataPoints[i];
                const p2 = fromDataPoints[nextI];
                const isNaEdge = !!(s.data[i].isNa || s.data[nextI].isNa);
                if (isNaEdge) return { p1, p2, isNaEdge };
                return {
                  p1: pullBack(p1, p2, cornerRadius),
                  p2: pullBack(p2, p1, cornerRadius),
                  isNaEdge,
                };
              })
            : null;

          return (
            <g
              key={
                animateOnDataChange ? `series-${seriesIndex}-${animKey}` : `series-${seriesIndex}`
              }
              style={{
                transformOrigin: `${center}px ${center}px`,
                animation: animateOnDataChange
                  ? 'radarGrow 0.6s ease-out forwards'
                  : seriesAnimatePathMs > 0
                  ? `radarRepairPreview ${seriesAnimatePathMs}ms cubic-bezier(0.2,0.8,0.2,1) forwards`
                  : undefined,
              }}>
              {s.segmentColors ? (
                <>
                  {/* Polychromatic fill — each edge subdivided into many thin flat-color
                      wedges (same interpolation as the stroke below), so the blend across
                      radial spokes reads smooth with no hard seam between categories. */}
                  {s.data.map((_, i) => {
                    const nextI = (i + 1) % s.data.length;
                    if (s.data[i].isNa || s.data[nextI].isNa) return null;
                    const p1 = getPointPosition(i, s.data[i].value);
                    const p2 = getPointPosition(nextI, s.data[nextI].value);
                    const fromP1 = fromDataPoints?.[i];
                    const fromP2 = fromDataPoints?.[nextI];
                    const colors = s.segmentColors as string[];
                    const c1 = colors[i];
                    const c2 = colors[nextI];
                    const FILL_SUB_STEPS = 16;
                    const wedges: React.ReactNode[] = [];
                    for (let step = 0; step < FILL_SUB_STEPS; step++) {
                      const t0 = step / FILL_SUB_STEPS;
                      const t1 = (step + 1) / FILL_SUB_STEPS;
                      const tMid = (t0 + t1) / 2;
                      const sx = p1.x + (p2.x - p1.x) * t0;
                      const sy = p1.y + (p2.y - p1.y) * t0;
                      const ex = p1.x + (p2.x - p1.x) * t1;
                      const ey = p1.y + (p2.y - p1.y) * t1;
                      const fromSx = fromP1
                        ? fromP1.x + ((fromP2?.x ?? fromP1.x) - fromP1.x) * t0
                        : sx;
                      const fromSy = fromP1
                        ? fromP1.y + ((fromP2?.y ?? fromP1.y) - fromP1.y) * t0
                        : sy;
                      const fromEx = fromP1
                        ? fromP1.x + ((fromP2?.x ?? fromP1.x) - fromP1.x) * t1
                        : ex;
                      const fromEy = fromP1
                        ? fromP1.y + ((fromP2?.y ?? fromP1.y) - fromP1.y) * t1
                        : ey;
                      const points = `${center},${center} ${sx},${sy} ${ex},${ey}`;
                      const fromPoints = `${center},${center} ${fromSx},${fromSy} ${fromEx},${fromEy}`;
                      wedges.push(
                        <polygon
                          key={`wedge-fill-${seriesIndex}-${i}-${step}`}
                          points={points}
                          fill={lerpColor(c1, c2, tMid)}
                          fillOpacity={0.4}>
                          {fromDataPoints && seriesAnimatePathMs > 0 && (
                            <animate
                              attributeName="points"
                              from={fromPoints}
                              to={points}
                              dur={`${seriesAnimatePathMs}ms`}
                              calcMode="spline"
                              keySplines="0.2 0.8 0.2 1"
                              fill="freeze"
                            />
                          )}
                        </polygon>,
                      );
                    }
                    return wedges;
                  })}
                  {s.data.map((_, i) => {
                    const nextI = (i + 1) % s.data.length;

                    // N/A edges: single dashed dim line, no gradient, full length (unrounded)
                    if (s.data[i].isNa || s.data[nextI].isNa) {
                      const p1 = dataPoints[i];
                      const p2 = dataPoints[nextI];
                      return (
                        <line
                          key={`seg-${seriesIndex}-${i}-na`}
                          x1={p1.x}
                          y1={p1.y}
                          x2={p2.x}
                          y2={p2.y}
                          stroke="rgba(255,255,255,0.2)"
                          strokeWidth={seriesStrokeWidth}
                          strokeDasharray="4,4"
                          strokeLinecap="round"
                        />
                      );
                    }
                    const { p1, p2 } = edgePulled[i];
                    const fromEdge = fromEdgePulled?.[i];
                    const colors = s.segmentColors as string[];
                    const c1 = colors[i];
                    const c2 = colors[nextI];

                    // Split each (corner-shortened) edge into SUB_STEPS mini-segments to simulate a smooth gradient
                    const SUB_STEPS = 8;
                    const subLines: React.ReactNode[] = [];
                    for (let step = 0; step < SUB_STEPS; step++) {
                      const t0 = step / SUB_STEPS;
                      const t1 = (step + 1) / SUB_STEPS;
                      const tMid = (t0 + t1) / 2;
                      const sx = p1.x + (p2.x - p1.x) * t0;
                      const sy = p1.y + (p2.y - p1.y) * t0;
                      const ex = p1.x + (p2.x - p1.x) * t1;
                      const ey = p1.y + (p2.y - p1.y) * t1;
                      const fromSx = fromEdge
                        ? fromEdge.p1.x + (fromEdge.p2.x - fromEdge.p1.x) * t0
                        : sx;
                      const fromSy = fromEdge
                        ? fromEdge.p1.y + (fromEdge.p2.y - fromEdge.p1.y) * t0
                        : sy;
                      const fromEx = fromEdge
                        ? fromEdge.p1.x + (fromEdge.p2.x - fromEdge.p1.x) * t1
                        : ex;
                      const fromEy = fromEdge
                        ? fromEdge.p1.y + (fromEdge.p2.y - fromEdge.p1.y) * t1
                        : ey;
                      // Interpolate color at midpoint of this sub-segment
                      const color = lerpColor(c1, c2, tMid);
                      subLines.push(
                        <line
                          key={`seg-${seriesIndex}-${i}-${step}`}
                          x1={sx}
                          y1={sy}
                          x2={ex}
                          y2={ey}
                          stroke={color}
                          strokeWidth={seriesStrokeWidth}
                          strokeLinecap="round">
                          {fromEdgePulled && seriesAnimatePathMs > 0 && (
                            <>
                              <animate
                                attributeName="x1"
                                from={String(fromSx)}
                                to={String(sx)}
                                dur={`${seriesAnimatePathMs}ms`}
                                calcMode="spline"
                                keySplines="0.2 0.8 0.2 1"
                                fill="freeze"
                              />
                              <animate
                                attributeName="y1"
                                from={String(fromSy)}
                                to={String(sy)}
                                dur={`${seriesAnimatePathMs}ms`}
                                calcMode="spline"
                                keySplines="0.2 0.8 0.2 1"
                                fill="freeze"
                              />
                              <animate
                                attributeName="x2"
                                from={String(fromEx)}
                                to={String(ex)}
                                dur={`${seriesAnimatePathMs}ms`}
                                calcMode="spline"
                                keySplines="0.2 0.8 0.2 1"
                                fill="freeze"
                              />
                              <animate
                                attributeName="y2"
                                from={String(fromEy)}
                                to={String(ey)}
                                dur={`${seriesAnimatePathMs}ms`}
                                calcMode="spline"
                                keySplines="0.2 0.8 0.2 1"
                                fill="freeze"
                              />
                            </>
                          )}
                        </line>,
                      );
                    }
                    return subLines;
                  })}
                  {/* Rounded corner joints — each vertex's incoming/outgoing edges share
                      the same color at that vertex, so a solid-color curve blends seamlessly. */}
                  {s.data.map((_, i) => {
                    const prevI = (i - 1 + s.data.length) % s.data.length;
                    const prevEdge = edgePulled[prevI];
                    const currEdge = edgePulled[i];
                    const fromPrevEdge = fromEdgePulled?.[prevI];
                    const fromCurrEdge = fromEdgePulled?.[i];
                    if (s.data[i].isNa || prevEdge.isNaEdge || currEdge.isNaEdge) return null;
                    const vertex = dataPoints[i];
                    const fromVertex = fromDataPoints?.[i];
                    const colors = s.segmentColors as string[];
                    const d = `M ${prevEdge.p2.x},${prevEdge.p2.y} Q ${vertex.x},${vertex.y} ${currEdge.p1.x},${currEdge.p1.y}`;
                    const fromD =
                      fromPrevEdge && fromCurrEdge && fromVertex
                        ? `M ${fromPrevEdge.p2.x},${fromPrevEdge.p2.y} Q ${fromVertex.x},${fromVertex.y} ${fromCurrEdge.p1.x},${fromCurrEdge.p1.y}`
                        : d;
                    return (
                      <path
                        key={`corner-${seriesIndex}-${i}`}
                        d={d}
                        fill="none"
                        stroke={colors[i]}
                        strokeWidth={seriesStrokeWidth}
                        strokeLinecap="round">
                        {fromEdgePulled && seriesAnimatePathMs > 0 && (
                          <animate
                            attributeName="d"
                            from={fromD}
                            to={d}
                            dur={`${seriesAnimatePathMs}ms`}
                            calcMode="spline"
                            keySplines="0.2 0.8 0.2 1"
                            fill="freeze"
                          />
                        )}
                      </path>
                    );
                  })}
                </>
              ) : (
                <>
                  {seriesFillColor !== 'transparent' && (
                    <path d={roundedPath} fill={seriesFillColor} stroke="none">
                      {fromRoundedPath && seriesAnimatePathMs > 0 && (
                        <animate
                          attributeName="d"
                          from={fromRoundedPath}
                          to={roundedPath}
                          dur={`${seriesAnimatePathMs}ms`}
                          calcMode="spline"
                          keySplines="0.2 0.8 0.2 1"
                          fill="freeze"
                        />
                      )}
                    </path>
                  )}
                  <path
                    d={roundedPath}
                    fill="none"
                    stroke={seriesStrokeColor}
                    strokeWidth={seriesStrokeWidth}
                    strokeDasharray={seriesStrokeDasharray}
                    strokeLinecap={seriesStrokeDasharray ? 'round' : undefined}
                    strokeLinejoin="round">
                    {fromRoundedPath && seriesAnimatePathMs > 0 && (
                      <animate
                        attributeName="d"
                        from={fromRoundedPath}
                        to={roundedPath}
                        dur={`${seriesAnimatePathMs}ms`}
                        calcMode="spline"
                        keySplines="0.2 0.8 0.2 1"
                        fill="freeze"
                      />
                    )}
                  </path>
                </>
              )}
              {contoured && seriesIndex === 0 && renderContour(dataPoints, roundedPath)}
            </g>
          );
        })}

        {/* Optional risk emphasis sits above the polygon while remaining
            non-interactive; the HTML vertex targets below still own hover/tap. */}
        {(dataSeries[0]?.data ?? []).map((point, index) => {
          if (!point.highlight || point.isNa) return null;
          const pos = getPointPosition(index, point.value);
          return (
            <g key={`highlight-${index}`}>
              {[0, 1].map((ring) => (
                <circle
                  key={ring}
                  cx={pos.x}
                  cy={pos.y}
                  r={5}
                  fill="none"
                  stroke="rgba(255,255,255,0.9)"
                  strokeWidth={1.25}
                  style={{
                    transformBox: 'fill-box',
                    transformOrigin: 'center',
                    animation: 'radarPointPulse 3s ease-out infinite',
                    animationDelay: `${ring * -1.5}s`,
                  }}
                />
              ))}
              <circle cx={pos.x} cy={pos.y} r={2.75} fill="rgba(255,255,255,0.8)" />
            </g>
          );
        })}
      </svg>

      {/* Vertex hit-targets — tap/hover a plotted point to see its label + score,
          mirroring HealthRings' tooltip. Used when axis labels are hidden (mobile). */}
      {showValueTooltips &&
        (dataSeries[0]?.data ?? []).map((point, index) => {
          const pos = getPointPosition(index, point.isNa ? 77 : point.value);
          return (
            <div
              key={`vertex-${index}`}
              onMouseEnter={
                !isTouchPrimary()
                  ? (e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltip({
                        kind: 'value',
                        label: point.label,
                        value: point.value,
                        isNa: !!point.isNa,
                        ...buildTooltipCoords(rect),
                      });
                    }
                  : undefined
              }
              onMouseLeave={() => {
                if (isTouchPrimary()) return;
                setTooltip(null);
              }}
              onClick={(e) => {
                if (!isTouchPrimary()) return;
                e.stopPropagation();
                if (tooltip) {
                  setTooltip(null);
                } else {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltip({
                    kind: 'value',
                    label: point.label,
                    value: point.value,
                    isNa: !!point.isNa,
                    ...buildTooltipCoords(rect),
                  });
                }
              }}
              style={{
                position: 'absolute',
                left: `${(pos.x / size) * 100}%`,
                top: `${(pos.y / size) * 100}%`,
                width: 28,
                height: 28,
                transform: 'translate(-50%, -50%)',
                borderRadius: '50%',
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
            />
          );
        })}

      {/* All-vertex frosted label overlay — externally triggered (forceShowAllLabels),
          label only, no value. Same tipStyle frosted-glass treatment as the single
          tooltip below, positioned at each axis's fixed outer endpoint (value=100,
          the far point of the graph) rather than the current data point — so
          labels sit at a consistent ring regardless of that axis's value, instead
          of drifting inward for lower-scoring axes. */}
      {forceShowAllLabels &&
        (dataSeries[0]?.data ?? []).map((point, index) => {
          const angle = startAngle + index * angleStep;
          const pos = getPointPosition(index, 100);
          // Push further out along the same radial direction so the tooltip
          // clears the grid boundary — at a high radiusFraction the grid can
          // sit just a few px inside the box, so the gap needs to be large
          // enough to read as clearly off the graph, not just past the line.
          const LABEL_OFFSET = 20;
          const extra = labelOffsetOverrides?.[index];
          const offsetX = pos.x + LABEL_OFFSET * Math.cos(angle) + (extra?.x ?? 0);
          const offsetY = pos.y + LABEL_OFFSET * Math.sin(angle) + (extra?.y ?? 0);
          return (
            <div
              key={`all-label-${index}`}
              style={{
                position: 'absolute',
                left: `${(offsetX / size) * 100}%`,
                top: `${(offsetY / size) * 100}%`,
                transform: 'translate(-50%, -50%)',
                boxSizing: 'border-box',
                background: 'var(--tx-glass)',
                backdropFilter: 'var(--tx-blur)',
                WebkitBackdropFilter: 'var(--tx-blur)',
                borderRadius: '12px',
                padding: '6px 12px',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
                color: 'var(--tx-text)',
                boxShadow: 'var(--tx-shadow-sm)',
                pointerEvents: 'none',
                zIndex: 5,
              }}>
              {axisLabel(point.label)}
            </div>
          );
        })}

      {/* HTML label overlay — enables hover tooltips */}
      {showLabels &&
        labelPositions.map((pos, index) => {
          const labelText = pos.label.replace(/_/g, ' ').toUpperCase();
          const tipText = labelTooltips?.[labelText];
          return (
            <div
              key={`label-${index}`}
              onMouseEnter={
                tipText
                  ? (e) => {
                      if (isTouchPrimary()) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltip({ kind: 'text', text: tipText, ...buildTooltipCoords(rect) });
                    }
                  : undefined
              }
              onMouseLeave={() => {
                if (isTouchPrimary()) return;
                setTooltip(null);
              }}
              onClick={
                tipText
                  ? (e) => {
                      if (!isTouchPrimary()) return;
                      e.stopPropagation();
                      if (tooltip) {
                        setTooltip(null);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({ kind: 'text', text: tipText, ...buildTooltipCoords(rect) });
                      }
                    }
                  : undefined
              }
              style={{
                position: 'absolute',
                left: pos.align === 'end' ? undefined : `${(pos.x / size) * 100}%`,
                right: pos.align === 'end' ? `${((size - pos.x) / size) * 100}%` : undefined,
                top: `${(pos.y / size) * 100}%`,
                transform: `translateY(-50%)${pos.align === 'middle' ? ' translateX(-50%)' : ''}`,
                cursor: tipText ? 'help' : 'default',
                padding: '4px 0',
                pointerEvents: 'auto',
              }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: axisLabelColor ?? 'var(--tx-muted)',
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                  borderBottom: tipText ? '1px dotted var(--tx-line)' : 'none',
                  paddingBottom: tipText ? 1 : 0,
                }}>
                {axisLabel(labelText)}
                {axisValues &&
                  (() => {
                    const pts = dataSeries[0]?.data ?? [];
                    const pt = pts[index];
                    if (!pt) return null;
                    const scored = pts.filter((p) => !p.isNa);
                    const weakest = scored.length > 1 && !pt.isNa && pt.value === Math.min(...scored.map((p) => p.value));
                    return (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 15,
                          fontWeight: 300,
                          letterSpacing: '-0.03em',
                          color: pt.isNa ? 'var(--tx-faint)' : weakest ? '#FF9A4D' : 'var(--tx-text)',
                        }}>
                        {pt.isNa ? 'N/A' : Math.round(pt.value)}
                      </span>
                    );
                  })()}
              </span>
            </div>
          );
        })}

      {/* Tooltip — portals into tooltipContainerRef (absolute, card-contained)
          when provided, otherwise falls back to document.body (fixed, viewport). */}
      {tooltip &&
        (() => {
          const EDGE_PAD = 12;
          const isValueTip = tooltip.kind === 'value';
          const TIP_HEIGHT_EST = isValueTip ? 56 : 80;
          const tipStyle: React.CSSProperties = {
            boxSizing: 'border-box',
            background: 'var(--tx-glass)',
            backdropFilter: 'var(--tx-blur)',
            WebkitBackdropFilter: 'var(--tx-blur)',
            borderRadius: '12px',
            padding: isValueTip ? '8px 14px' : '12px 16px',
            fontSize: 12,
            color: 'var(--tx-text)',
            lineHeight: 1.55,
            boxShadow: 'var(--tx-shadow-sm)',
            pointerEvents: 'none',
            fontWeight: 400,
          };
          const content = isValueTip ? (
            <>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tx-faint)',
                  marginBottom: 2,
                  whiteSpace: 'nowrap',
                }}>
                {tooltip.label.replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--tx-text)', lineHeight: 1 }}>
                {tooltip.isNa ? (
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>N/A</span>
                ) : (
                  <>
                    {Math.round(tooltip.value)}
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 400,
                        color: 'var(--tx-faint)',
                        marginLeft: 2,
                      }}>
                      /100
                    </span>
                  </>
                )}
              </div>
            </>
          ) : (
            tooltip.text
          );

          if (tooltipContainerRef?.current) {
            const container = tooltipContainerRef.current;
            const containerW = container.offsetWidth;
            const TIP_WIDTH = isValueTip ? 110 : 260;
            const HALF_W = TIP_WIDTH / 2;
            const clampedX = Math.max(
              HALF_W + EDGE_PAD,
              Math.min(tooltip.x, containerW - HALF_W - EDGE_PAD),
            );
            const flipBelow = tooltip.y - TIP_HEIGHT_EST < EDGE_PAD;
            return createPortal(
              <div
                style={{
                  ...tipStyle,
                  position: 'absolute',
                  left: clampedX,
                  top: flipBelow ? tooltip.yBottom + 8 : tooltip.y - 10,
                  transform: flipBelow ? 'translateX(-50%)' : 'translate(-50%, -100%)',
                  width: TIP_WIDTH,
                  zIndex: 10,
                }}>
                {content}
              </div>,
              container,
            );
          }

          const VIEWPORT_W = typeof window !== 'undefined' ? window.innerWidth : 1024;
          const IS_MOBILE = VIEWPORT_W < 900;
          const TIP_WIDTH = isValueTip
            ? 110
            : IS_MOBILE
            ? Math.min(280, VIEWPORT_W - 2 * EDGE_PAD)
            : 260;
          const HALF_W = TIP_WIDTH / 2;
          const clampedX = Math.max(
            HALF_W + EDGE_PAD,
            Math.min(tooltip.x, VIEWPORT_W - HALF_W - EDGE_PAD),
          );
          return createPortal(
            <div
              style={{
                ...tipStyle,
                position: 'fixed',
                left: clampedX,
                top: tooltip.y - 10,
                transform: 'translate(-50%, -100%)',
                width: TIP_WIDTH,
                zIndex: 9999,
              }}>
              {content}
            </div>,
            document.body,
          );
        })()}
    </Box>
  );
};

export default RadarChart;
